import React from "react";
import { useLanguage } from "@/App";
import { Users, Award, Rocket, CheckCircle } from "lucide-react";

const About = () => {
  const { t, language } = useLanguage();
  const about = t("about");

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#121212] relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#FF6B00]/5 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Column - Content */}
          <div>
            <p
              data-testid="about-overline"
              className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4"
            >
              {about.overline}
            </p>
            <h2
              data-testid="about-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {about.title}
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed mb-8">
              {about.description}
            </p>

            {/* Director Card */}
            <div 
              data-testid="director-card"
              className="bg-[#0A0A0A] border border-white/10 p-6 inline-flex items-center gap-4"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B00] to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-black">
                SF
              </div>
              <div>
                <p className="text-white font-bold text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {about.team.director}
                </p>
                <p className="text-zinc-500 text-sm">{about.team.role}</p>
              </div>
            </div>

            {/* Key Points */}
            <div className="mt-8 space-y-3">
              {(language === "nl" ? [
                "Expert team van developers & designers",
                "Gespecialiseerd in AI & automatisering",
                "Full-stack software ontwikkeling",
                "Resultaatgerichte aanpak"
              ] : [
                "Expert team of developers & designers",
                "Specialized in AI & automation",
                "Full-stack software development",
                "Results-driven approach"
              ]).map((point, index) => (
                <div key={index} className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle size={18} className="text-[#FF6B00]" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Stats Grid */}
          <div className="grid grid-cols-2 gap-px bg-white/10">
            {about.stats.map((stat, index) => {
              const icons = [
                <Rocket size={24} className="text-[#FF6B00]" />,
                <Users size={24} className="text-purple-400" />,
                <Award size={24} className="text-blue-400" />,
                <CheckCircle size={24} className="text-green-400" />
              ];

              return (
                <div
                  key={index}
                  data-testid={`about-stat-${index}`}
                  className="bg-[#121212] p-8 md:p-10 text-center group hover:bg-[#1a1a1a] transition-colors"
                >
                  <div className="flex justify-center mb-4">
                    {icons[index]}
                  </div>
                  <p
                    className="text-4xl md:text-5xl font-black text-white mb-2"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {stat.value}
                  </p>
                  <p className="text-zinc-500 text-sm uppercase tracking-wider">
                    {stat.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Banner */}
        <div className="mt-24 bg-[#0A0A0A] border border-white/10 p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              {/* Avatar Stack */}
              <div className="flex -space-x-3">
                {['SF', 'JD', 'MK', 'AR', 'LV'].map((initials, index) => (
                  <div
                    key={index}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 border-2 border-[#0A0A0A] flex items-center justify-center text-xs font-bold text-white"
                  >
                    {initials}
                  </div>
                ))}
                <div className="w-10 h-10 rounded-full bg-[#FF6B00] border-2 border-[#0A0A0A] flex items-center justify-center text-xs font-bold text-white">
                  +10
                </div>
              </div>
              <div>
                <p className="text-white font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {language === "nl" ? "Ons Development Team" : "Our Development Team"}
                </p>
                <p className="text-zinc-500 text-sm">
                  {language === "nl" ? "15+ experts klaar om jouw visie te bouwen" : "15+ experts ready to build your vision"}
                </p>
              </div>
            </div>
            <a
              href="#contact"
              data-testid="about-cta"
              className="bg-white/5 border border-white/10 text-white px-6 py-3 text-sm font-bold hover:bg-white/10 transition-all"
            >
              {language === "nl" ? "Werk Met Ons" : "Work With Us"}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
